<template>
  <div class="my-3">
    <!-- Optional: If you want a header -->
    <EquipmentHeader v-if="title">
      <template #title>
        <div
          class="flex justify-content-between align-items-center flex-wrap pb-2"
        >
          {{ title }}
        </div>
      </template>
    </EquipmentHeader>

    <div class="context-menu">
      <div class="flex gap-2 align-items-center p-2">
        <InputSwitch v-model="localEnabled" />
        <span>{{ toggleLabel }}</span>
      </div>

      <!-- If toggle is on, show the file upload, link, table, etc. -->
      <div v-if="localEnabled">
        <div v-if="!hasData" class="mt-3 flex flex-col gap-2">
          <!-- Hidden file input, for fallback if needed -->
          <input
            ref="hiddenFileInput"
            type="file"
            :accept="accept"
            style="display: none"
            @change="handleFileSelected"
          />
          <FileInput
            :key="fileInputKey"
            :icon="uploadIcon"
            :label="uploadLabel"
            :auto="true"
            :accept="accept"
            :maxFileSize="maxFileSize"
            :show-cancel-button="true"
            data-cy="generic-uploader"
            @change="handleFileSelected"
          />

          <!-- Download link to the template, if provided -->
          <a
            v-if="templateDownloadUrl"
            :href="templateDownloadUrl"
            :download="templateDownloadName"
            class="mt-3"
          >
            <icon :icon="['far', 'link']" />
            <span class="ml-1">{{ templateLinkText }}</span>
          </a>
        </div>

        <div v-else class="flex gap-2 align-items-center p-2">
          <Button class="p-button-danger mr-2" @click="onClear">
            Clear Data
          </Button>
        </div>

        <div v-if="hasData" class="mt-4">
          <DataTable :value="tableData" paginator :rows="10">
            <Column
              v-for="col in columns"
              :key="col.field"
              :field="col.field"
              :header="col.header"
            />
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import InputSwitch from "primevue/inputswitch";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import FileInput from "@/components/FileInput.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";

const props = withDefaults(
  defineProps<{
    title?: string;
    toggleLabel: string;
    modelValue: boolean;
    fileInputKey?: number;
    tableData: Array<Record<string, unknown>>;
    columns: Array<{ field: string; header: string }>;
    accept?: string;
    uploadLabel?: string;
    uploadIcon?: string;
    maxFileSize?: number;
    templateDownloadUrl?: string;
    templateDownloadName?: string;
    templateLinkText?: string;

    onFileSelected: (file: File) => void;
    onClear: () => void;
  }>(),
  {
    title: "",
    fileInputKey: () => Date.now(),
    accept: ".xlsx, .xls",
    uploadLabel: "Add Fluid Properties Sheet",
    uploadIcon: "fas fa-plus-circle",
    maxFileSize: 5000000,
    templateDownloadUrl: "",
    templateDownloadName: "",
    templateLinkText: "Fluid Properties Template"
  }
);

const emit = defineEmits<{
  // We allow parent to do v-model usage: <GenericUploadTable v-model="myBool"/>
  (e: "update:modelValue", value: boolean): void;
}>();

const hiddenFileInput = ref<HTMLInputElement | null>(null);
const localEnabled = computed({
  get: () => props.modelValue,
  set: val => emit("update:modelValue", val)
});

const hasData = computed(() => props.tableData && props.tableData.length > 0);

function handleFileSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  if (!target.files || target.files.length === 0) {
    return;
  }
  const file = target.files[0];
  // pass the file back up to the parent's parse logic
  props.onFileSelected(file);
}
</script>
